import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  Lock, Loader2, Save, LogOut, ExternalLink, Palette, Type, Languages, Mail, Send, Info,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_ORIGIN = window.location.origin;

const LANGS = [
  { code: "en", label: "English" },
  { code: "nl", label: "Nederlands" },
  { code: "fr", label: "Français" },
];

const COLOR_FIELDS = [
  { key: "background", label: "Background" },
  { key: "text", label: "Text" },
  { key: "button", label: "Button" },
  { key: "buttonText", label: "Button text" },
  { key: "warning", label: "Warning (14 days)" },
  { key: "error", label: "Error" },
  { key: "success", label: "Success" },
];

const TEXT_FIELDS = [
  { key: "badge", label: "Badge (small label above title)" },
  { key: "title", label: "Title" },
  { key: "subtitle", label: "Subtitle", area: true },
  { key: "orderLabel", label: "Order number — label" },
  { key: "orderPlaceholder", label: "Order number — placeholder" },
  { key: "orderRequired", label: "Order number — required error" },
  { key: "postalLabel", label: "Postal code — label" },
  { key: "postalPlaceholder", label: "Postal code — placeholder" },
  { key: "postalRequired", label: "Postal code — required error" },
  { key: "emailLabel", label: "Email — label" },
  { key: "emailPlaceholder", label: "Email — placeholder" },
  { key: "emailInvalid", label: "Email — invalid error" },
  { key: "verifyButton", label: "Verify button" },
  { key: "recallButton", label: "Recall button" },
  { key: "orderVerified", label: "Order verified label" },
  { key: "warning14", label: "Older-than-14-days warning", area: true },
  { key: "reasonNotFound", label: "Order not found message", area: true },
  { key: "reasonCancelled", label: "Order cancelled message", area: true },
  { key: "reasonCompany", label: "Company order (not eligible) message", area: true },
  { key: "successTitle", label: "Success — title" },
  { key: "successBodyPre", label: "Success — text before email", area: true },
  { key: "successBodyPost", label: "Success — text after email", area: true },
  { key: "closeButton", label: "Close button" },
  { key: "genericError", label: "Generic error (toast)", area: true },
  { key: "submitError", label: "Submit error (toast)", area: true },
];

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("admin_token") || "");
  const [screen, setScreen] = useState("popup");
  const [config, setConfig] = useState(null);
  const [emailCfg, setEmailCfg] = useState(null);
  const [activeLang, setActiveLang] = useState("nl");
  const [emailLang, setEmailLang] = useState("nl");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("admin_token");
    setToken("");
    setConfig(null);
    setEmailCfg(null);
  }, []);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const [w, e] = await Promise.all([
        axios.get(`${API}/widget/config`),
        axios.get(`${API}/admin/email-config`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setConfig(w.data);
      setEmailCfg(e.data);
    } catch (err) {
      if (err?.response?.status === 401) handleLogout();
      else toast.error("Could not load configuration.");
    } finally {
      setLoading(false);
    }
  }, [token, handleLogout]);

  useEffect(() => {
    if (token) loadConfig();
  }, [token, loadConfig]);

  // --- popup config setters ---
  const setColor = (key, value) =>
    setConfig((c) => ({ ...c, styles: { ...c.styles, colors: { ...c.styles.colors, [key]: value } } }));
  const setFont = (key, value) =>
    setConfig((c) => ({
      ...c,
      styles: { ...c.styles, fontSize: { ...c.styles.fontSize, [key]: Number(value) || 0 } },
    }));
  const setText = (lang, key, value) =>
    setConfig((c) => ({ ...c, texts: { ...c.texts, [lang]: { ...c.texts[lang], [key]: value } } }));

  // --- email config setters ---
  const setEmailField = (key, value) => setEmailCfg((c) => ({ ...c, [key]: value }));
  const setEmailTemplate = (lang, key, value) =>
    setEmailCfg((c) => ({
      ...c,
      templates: { ...c.templates, [lang]: { ...c.templates[lang], [key]: value } },
    }));

  const handleSavePopup = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/widget/config`,
        { texts: config.texts, styles: config.styles },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConfig(data);
      toast.success("Saved. Popup changes are live for customers.");
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("Session expired. Log in again."); handleLogout(); }
      else toast.error("Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEmail = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/email-config`,
        { fromName: emailCfg.fromName, fromEmail: emailCfg.fromEmail, copyEmail: emailCfg.copyEmail, templates: emailCfg.templates },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEmailCfg(data);
      toast.success("Email settings saved.");
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("Session expired. Log in again."); handleLogout(); }
      else toast.error("Could not save email settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/email-config/test`,
        { to: testTo, lang: emailLang, config: { fromName: emailCfg.fromName, fromEmail: emailCfg.fromEmail, copyEmail: emailCfg.copyEmail, templates: emailCfg.templates } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(data.mock ? "Test rendered & logged (SMTP not configured yet)." : `Test email sent to ${data.to}.`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not send test email.");
    } finally {
      setTesting(false);
    }
  };

  const onSave = () => (screen === "popup" ? handleSavePopup() : handleSaveEmail());

  if (!token) {
    return <Login onToken={(tk) => { localStorage.setItem("admin_token", tk); setToken(tk); }} />;
  }

  return (
    <div className="min-h-screen bg-stone-50 text-[#0A0A0A] font-sans">
      <Toaster position="top-center" />

      <header className="sticky top-0 z-10 border-b border-black/10 bg-white">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="font-heading font-extrabold tracking-tight text-lg">Recall widget editor</span>
          <div className="flex items-center gap-2">
            {screen === "popup" && (
              <a
                href={`${APP_ORIGIN}/?embed=1&lang=${activeLang}`}
                target="_blank"
                rel="noreferrer"
                data-testid="admin-preview-link"
                className="h-10 px-4 flex items-center gap-2 border border-black/15 text-sm font-semibold hover:bg-stone-100"
              >
                <ExternalLink className="w-4 h-4" /> Preview
              </a>
            )}
            <button
              data-testid="admin-save-btn"
              onClick={onSave}
              disabled={saving || (screen === "popup" ? !config : !emailCfg)}
              className="h-10 px-5 flex items-center gap-2 bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-black/80 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="h-10 w-10 flex items-center justify-center border border-black/15 hover:bg-stone-100"
              aria-label="Log out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Screen nav */}
      <div className="border-b border-black/10 bg-white">
        <div className="max-w-5xl mx-auto px-6 flex gap-1">
          {[["popup", "Popup", Palette], ["email", "Email", Mail]].map(([k, label, Icon]) => (
            <button
              key={k}
              data-testid={`nav-${k}`}
              onClick={() => setScreen(k)}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px flex items-center gap-2 ${
                screen === k ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-[#525252] hover:text-black"
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
      </div>

      {loading || !config || !emailCfg ? (
        <div className="max-w-5xl mx-auto px-6 py-24 flex items-center gap-3 text-[#525252]">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading…
        </div>
      ) : screen === "popup" ? (
        <main className="max-w-5xl mx-auto px-6 py-10 flex flex-col gap-10">
          <section className="bg-white border border-black/10 p-6">
            <h2 className="font-heading font-bold text-xl flex items-center gap-2 mb-5">
              <Palette className="w-5 h-5" /> Colors
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
              {COLOR_FIELDS.map((f) => (
                <div key={f.key} className="flex flex-col gap-2">
                  <label className="text-xs uppercase tracking-widest font-bold text-[#525252]">{f.label}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      data-testid={`color-${f.key}`}
                      value={config.styles.colors[f.key]}
                      onChange={(e) => setColor(f.key, e.target.value)}
                      className="h-10 w-12 rounded border border-black/15 bg-white cursor-pointer"
                    />
                    <input
                      type="text"
                      value={config.styles.colors[f.key]}
                      onChange={(e) => setColor(f.key, e.target.value)}
                      className="h-10 flex-1 px-2 border border-black/15 text-sm font-mono uppercase"
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-white border border-black/10 p-6">
            <h2 className="font-heading font-bold text-xl flex items-center gap-2 mb-5">
              <Type className="w-5 h-5" /> Font sizes
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
              {[{ key: "title", label: "Title size (px)" }, { key: "body", label: "Body size (px)" }].map((f) => (
                <div key={f.key} className="flex flex-col gap-2">
                  <label className="text-xs uppercase tracking-widest font-bold text-[#525252]">{f.label}</label>
                  <input
                    type="number"
                    min="8"
                    max="80"
                    data-testid={`font-${f.key}`}
                    value={config.styles.fontSize[f.key]}
                    onChange={(e) => setFont(f.key, e.target.value)}
                    className="h-10 px-3 border border-black/15 text-sm"
                  />
                </div>
              ))}
            </div>
          </section>

          <section className="bg-white border border-black/10 p-6">
            <h2 className="font-heading font-bold text-xl flex items-center gap-2 mb-5">
              <Languages className="w-5 h-5" /> Texts
            </h2>
            <div className="flex gap-1 mb-6">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  data-testid={`text-lang-${l.code}`}
                  onClick={() => setActiveLang(l.code)}
                  className={`px-4 py-2 text-sm font-semibold transition-colors ${
                    activeLang === l.code ? "bg-[#0A0A0A] text-white" : "text-[#525252] hover:bg-stone-100"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {TEXT_FIELDS.map((f) => (
                <div key={f.key} className={`flex flex-col gap-1.5 ${f.area ? "md:col-span-2" : ""}`}>
                  <label className="text-xs uppercase tracking-widest font-bold text-[#525252]">{f.label}</label>
                  {f.area ? (
                    <textarea
                      data-testid={`text-${activeLang}-${f.key}`}
                      value={config.texts[activeLang][f.key] || ""}
                      onChange={(e) => setText(activeLang, f.key, e.target.value)}
                      rows={2}
                      className="px-3 py-2 border border-black/15 text-sm resize-y"
                    />
                  ) : (
                    <input
                      type="text"
                      data-testid={`text-${activeLang}-${f.key}`}
                      value={config.texts[activeLang][f.key] || ""}
                      onChange={(e) => setText(activeLang, f.key, e.target.value)}
                      className="h-10 px-3 border border-black/15 text-sm"
                    />
                  )}
                </div>
              ))}
            </div>
          </section>
        </main>
      ) : (
        <EmailEditor
          emailCfg={emailCfg}
          emailLang={emailLang}
          setEmailLang={setEmailLang}
          setEmailField={setEmailField}
          setEmailTemplate={setEmailTemplate}
          testTo={testTo}
          setTestTo={setTestTo}
          onTest={handleTestEmail}
          testing={testing}
        />
      )}
    </div>
  );
}

function EmailEditor({ emailCfg, emailLang, setEmailLang, setEmailField, setEmailTemplate, testTo, setTestTo, onTest, testing }) {
  const variables = emailCfg.variables || [];
  const tpl = emailCfg.templates[emailLang] || { subject: "", body: "" };

  return (
    <main className="max-w-5xl mx-auto px-6 py-10 flex flex-col gap-10">
      {!emailCfg.smtp_configured && (
        <div className="flex items-start gap-3 bg-[#FFF4E5] border border-[#FFCC00] text-[#8a5a00] px-4 py-3 text-sm" data-testid="smtp-warning">
          <Info className="w-5 h-5 shrink-0 mt-0.5" />
          <span>SMTP is not configured yet, so emails are only logged (not delivered). Add SMTP credentials in the backend to send real emails. Templates and a test send still work.</span>
        </div>
      )}

      {/* Settings */}
      <section className="bg-white border border-black/10 p-6">
        <h2 className="font-heading font-bold text-xl flex items-center gap-2 mb-5">
          <Mail className="w-5 h-5" /> Email settings
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {[
            { key: "fromName", label: "From name", testid: "email-fromName" },
            { key: "fromEmail", label: "From email", testid: "email-fromEmail" },
            { key: "copyEmail", label: "Copy to (BCC)", testid: "email-copyEmail" },
          ].map((f) => (
            <div key={f.key} className="flex flex-col gap-1.5">
              <label className="text-xs uppercase tracking-widest font-bold text-[#525252]">{f.label}</label>
              <input
                type="text"
                data-testid={f.testid}
                value={emailCfg[f.key] || ""}
                onChange={(e) => setEmailField(f.key, e.target.value)}
                className="h-10 px-3 border border-black/15 text-sm"
              />
            </div>
          ))}
        </div>
      </section>

      {/* Variables helper */}
      <section className="bg-white border border-black/10 p-6">
        <h2 className="font-heading font-bold text-xl flex items-center gap-2 mb-3">
          <Info className="w-5 h-5" /> Available variables
        </h2>
        <p className="text-sm text-[#525252] mb-4">
          Use these in the subject and body — they are replaced with the real order/recall values when the email is sent.
          <br />
          <span className="text-xs">Note: <b>timestamp</b> is the moment the recall is received, in CET/CEST (Europe/Amsterdam).</span>
        </p>
        <div className="flex flex-wrap gap-2">
          {variables.map((v) => (
            <code
              key={v}
              data-testid={`email-var-${v}`}
              className="px-2.5 py-1 bg-stone-100 border border-black/10 text-xs font-mono"
            >
              {`{{${v}}}`}
            </code>
          ))}
        </div>
      </section>

      {/* Templates */}
      <section className="bg-white border border-black/10 p-6">
        <h2 className="font-heading font-bold text-xl flex items-center gap-2 mb-5">
          <Languages className="w-5 h-5" /> Templates
        </h2>
        <div className="flex gap-1 mb-6">
          {LANGS.map((l) => (
            <button
              key={l.code}
              data-testid={`email-lang-${l.code}`}
              onClick={() => setEmailLang(l.code)}
              className={`px-4 py-2 text-sm font-semibold transition-colors ${
                emailLang === l.code ? "bg-[#0A0A0A] text-white" : "text-[#525252] hover:bg-stone-100"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs uppercase tracking-widest font-bold text-[#525252]">Subject</label>
            <input
              type="text"
              data-testid={`email-subject-${emailLang}`}
              value={tpl.subject || ""}
              onChange={(e) => setEmailTemplate(emailLang, "subject", e.target.value)}
              className="h-10 px-3 border border-black/15 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs uppercase tracking-widest font-bold text-[#525252]">Body</label>
            <textarea
              data-testid={`email-body-${emailLang}`}
              value={tpl.body || ""}
              onChange={(e) => setEmailTemplate(emailLang, "body", e.target.value)}
              rows={10}
              className="px-3 py-2 border border-black/15 text-sm font-mono resize-y leading-relaxed"
            />
          </div>
        </div>
      </section>

      {/* Test send */}
      <section className="bg-white border border-black/10 p-6">
        <h2 className="font-heading font-bold text-xl flex items-center gap-2 mb-5">
          <Send className="w-5 h-5" /> Send a test
        </h2>
        <p className="text-sm text-[#525252] mb-4">
          Sends the <b>{LANGS.find((l) => l.code === emailLang)?.label}</b> template with sample order data to the address below.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            data-testid="email-test-to"
            placeholder="you@email.com"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            className="h-11 px-3 border border-black/15 text-sm flex-1"
          />
          <button
            data-testid="email-test-btn"
            onClick={onTest}
            disabled={testing || !testTo}
            className="h-11 px-6 flex items-center justify-center gap-2 bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-black/80 disabled:opacity-50"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send test
          </button>
        </div>
      </section>
    </main>
  );
}

function Login({ onToken }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      onToken(data.token);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4 font-sans">
      <Toaster position="top-center" />
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-white border border-black/10 p-8 flex flex-col gap-5"
        data-testid="admin-login-form"
      >
        <div className="flex flex-col gap-1">
          <div className="w-11 h-11 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center mb-2">
            <Lock className="w-5 h-5" />
          </div>
          <h1 className="font-heading font-extrabold tracking-tight text-2xl">Widget editor</h1>
          <p className="text-sm text-[#525252]">Sign in to edit the recall popup.</p>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs uppercase tracking-widest font-bold text-[#525252]">Email</label>
          <input
            type="email"
            data-testid="admin-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-12 px-3 border border-black/15 text-sm"
            required
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs uppercase tracking-widest font-bold text-[#525252]">Password</label>
          <input
            type="password"
            data-testid="admin-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-12 px-3 border border-black/15 text-sm"
            required
          />
        </div>
        {error && (
          <p className="text-sm text-[#E50000]" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          data-testid="admin-login-btn"
          disabled={busy}
          className="h-12 bg-[#0A0A0A] text-white font-semibold hover:bg-black/80 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign in"}
        </button>
      </form>
    </div>
  );
}
